import React, { Component } from 'react';
import ReactLoading from 'react-loading';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { get, isInteger } from 'lodash-es';

import ChannelRadioGroup from './ChannelRadioGroup';
import CategoryAccordion from './CategoryAccordion';
import InlineAlert from '../../../utilities/form-components/InlineAlert';

const noContactsText = 'You do not have any saved contacts.  Please go to the Users and Permissions page to add new contacts.';
const errorLoadingText = 'Unable to load your session details. Please try again later or contact Customer Care any time at:';
const errorSubmittingText = 'Unable to submit session details. Please try again later or contact Customer Care any time at:';
const audioVideoTooltipText = 'Depending on your contract, additional fees may apply.';

class Step2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bpSteps: {
        from: '',
        to: '',
        stepPct: ''
      },
      loading: true,
      selectedCategories: new Set(),
      showNoContactsError: false,
      showLoadingError: false,
      showSubmitError: false
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { catalogID, groupID }
      }
    } = this.props;

    // Step 1: Get the Summary Information for the Catalog.  This contains everything done so far
    axios(`/cps/app/catalog/${catalogID}?_=${new Date().getTime()}`)
      .then(summaryResp => {
        const summary = get(summaryResp, 'data.data.sessionDefinition', null);

        /**
         * Step 2: Grab stuff from the summary, we need the addressID and houseID
         * to make our next requests.
         */
        const {
          currency: { code: currencyCode, name: currencyName },
          contacts: summaryContacts,
          viewNotes: summaryViewNotes,
          addressID,
          ampm,
          audioVideo,
          categoryIDs,
          creator,
          date,
          dateEnd,
          description,
          hour,
          houseID,
          isTimed,
          minute,
          resellUnsoldLiveLots,
          timedType,
          timedValue,
          title
        } = summary;

        /**
         * Step 3: Make a bunch of async requests to get:
         * 1: Default contacts
         * 2: The list of all contacts
         * 3: Buyers Premium Information
         * 4: The list of channels for the Post Sale To section
         * 5: The list of categories for the collapse accordion
         */
        const timeStamp = new Date().getTime();
        axios
          .all([
            axios(`/cps/app/house/${houseID}/contacts?_=${timeStamp}`),
            axios(`/cps/app/house/${houseID}/address/${addressID}/defaults`),
            axios(`/cps/app/group/${groupID}/buypremsteps`),
            axios(`/cps/app/catalog/${catalogID}/channels?_=${timeStamp}`),
            axios(`/cps/app/house/${houseID}/categories`)
          ])
          .then(
            axios.spread((contactsResp, defaultResp, bpStepResp, channelsResp, categoriesResp) => {
              const contacts = get(contactsResp, 'data.data.contacttypes', null);

              /**
               * The user must have contacts set prior to completing this step.
               * Show an error and make them add contacts first.
               */
              if (!contacts) {
                this.setState({ showNoContactsError: true, loading: false });
                return;
              }

              const defaults = get(defaultResp, 'data.data.address.defaults', null);
              const bpSteps = get(bpStepResp, 'data.data.buypremsteps[0]', null);

              const channels = get(channelsResp, 'data.data.channels', null);

              /**
               * The BE sends a state array as following:
               *
               * states: [0];
               * or
               * states: [0, 2, 1];
               *
               * The numbers mean something - there's probably documentation somewhere???  I haven't
               * yet found it.  Who knows why array[1] = 2 and array[2] = 1.  It's just another
               * Invaluable mystery.
               *
               * Regardless we need to default our value to the states value if it only has a length
               * of 1 which indicates the option was already set.  Again I'm not really sure why but
               * if you don't - the database won't be in sync for IB houses and TEC
               * tickets will appear.
               *
               * If the array is longer than 1, then the user gets the radio buttons and can choose.
               */
              channels.forEach(channel => {
                if (channel.states.length === 1) {
                  channel.value = channel.states[0];
                } else {
                  channel.value = 0;
                }
              });

              // As per UX we want the first collapse panel opened on initial load
              const categories = get(categoriesResp, 'data.data.categories', null);
              categories.map(category => (category.isOpen = false));
              categories[0].isOpen = true;

              const {
                consignContactID,
                expertContactID,
                saleAdminID,
                viewNotes: defaultViewNotes
              } = defaults;

              // Map contacts so we can place them in the appropriate select boxes
              let consignContacts = null;
              let saleAdminContacts = null;
              let expertContacts = null;
              contacts.forEach(contactList => {
                if (contactList.type.indexOf('Consignment') >= 0) {
                  consignContacts = contactList.contacts;
                } else if (contactList.type.indexOf('Sale') >= 0) {
                  saleAdminContacts = contactList.contacts;
                } else if (contactList.type.indexOf('Expert') >= 0) {
                  expertContacts = contactList.contacts;
                }
              });

              /**
               * This utility function takes the list of contacts, moves the matching id
               * to the first element, and returns the new array.
               *
               * If the summary already has the selected contacts, the user is editing a session
               * so default to them.
               *
               * Otherwise we use the id from the defaults API.  This is the reason for writing
               * the ||, i.e. summaryConsign || consignContactID
               */
              const orderContacts = (contactList, id) => {
                const selectedItem = contactList.filter(contact => contact.id === id)[0];
                const remainingItems = contactList.filter(contact => contact.id !== id);

                if (selectedItem) {
                  return [selectedItem, ...remainingItems];
                }
                // The user has not selected any defaults, so just return the original list
                return contactList;
              };

              const summaryConsign = summaryContacts['1'];
              const summarySaleAdmin = summaryContacts['3'];
              const summaryExpert = summaryContacts['4'];

              /**
               * Get the user's contact ids from the ingest
               * if they exist, if not fall back on the user's defaults
               */
              const orderedConsigns = orderContacts(
                consignContacts,
                summaryConsign || consignContactID
              );
              const orderedSaleAdmins = orderContacts(
                saleAdminContacts,
                summarySaleAdmin || saleAdminID
              );
              const orderedExperts = orderContacts(
                expertContacts,
                summaryExpert || expertContactID
              );

              this.setState({
                addressID,
                ampm,
                audioVideo,
                bpSteps,
                categories,
                channels,
                creator,
                currencyCode,
                currencyName,
                date,
                dateEnd,
                defaults,
                description,
                hour,
                houseID,
                isTimed,
                minute,
                orderedConsigns,
                orderedExperts,
                orderedSaleAdmins,
                resellUnsoldLiveLots,
                timedType,
                timedValue,
                title,
                consignmentContact: orderedConsigns[0].id,
                consignContactCheckbox: isInteger(consignContactID) && consignContactID > 0,
                expertContact: orderedExperts[0].id,
                expertContactCheckbox: isInteger(expertContactID) && expertContactID > 0,
                saleAdministratorContact: orderedSaleAdmins[0].id,
                saleAdministratorContactCheckbox: isInteger(saleAdminID) && saleAdminID > 0,
                selectedCategories: new Set(categoryIDs),
                viewNotes: summaryViewNotes || defaultViewNotes || '',
                viewNotesCheckbox: defaultViewNotes.length >= 1,
                loading: false
              });
            })
          )
          .catch(() => {
            this.setState({ showLoadingError: true, loading: false });
          });
      })
      .catch(() => {
        this.setState({ showLoadingError: true, loading: false });
      });
  }

  /**
   * We have to update the categories in the parent component, and track the props for
   * whether or not the accordion is open or closed here.  Otherwise when the user checks
   * a checkbox and triggers a re-render, we will revert all collapse panels to closed.
   */
  toggleAccordion = index => {
    const { categories } = this.state;
    categories[index].isOpen = !categories[index].isOpen;

    this.setState({ categories });
  };

  handleCategories = e => {
    const { selectedCategories } = this.state;
    const categoryValue = parseInt(e.target.value, 10);

    /**
     * ES6 Sets are awesome!
     *
     * If we already have the value in the array, delete it (user clicked a checkmark)
     * Otherwise, if we have fewer than 8 items, add to the array.
     */
    if (selectedCategories.has(categoryValue)) {
      selectedCategories.delete(categoryValue);
    } else if (selectedCategories.size < 8) {
      selectedCategories.add(categoryValue);
    }

    this.setState({ selectedCategories });
  };

  handleCheckbox = e => {
    const { id } = e.target;
    this.setState({
      [id]: !this.state[id]
    });
  };

  handleAvRadio = value => {
    this.setState({ audioVideo: value });
  };

  handleRadioChange = (id, value) => {
    const { channels } = this.state;
    const channelIndex = channels.findIndex(channel => channel.channelID === id);
    channels[channelIndex].value = value;

    this.setState({ channels });
  };

  handleSelectContact = e => {
    const { id, value } = e.target;
    this.setState({
      [id]: parseInt(value, 10)
    });
  };

  handleTextArea = e => {
    const { id, value } = e.target;
    this.setState({
      [id]: value
    });
  };

  submitStepTwo = async (option = 'continue') => {
    const {
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step6';
    const stepNumber = isEditingSession ? 6 : 3;

    const {
      addressID,
      ampm,
      channels,
      consignContactCheckbox,
      consignmentContact,
      creator,
      date,
      dateEnd,
      description,
      expertContact,
      expertContactCheckbox,
      hour,
      houseID,
      isTimed,
      minute,
      resellUnsoldLiveLots,
      saleAdministratorContact,
      saleAdministratorContactCheckbox,
      selectedCategories,
      timedType,
      timedValue,
      title,
      viewNotes,
      viewNotesCheckbox
    } = this.state;

    const postHeaders = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    };

    // This is required for several requests apparently, so use this as a req body template
    const ingestRequestBody = {
      addressID,
      ampm,
      creator,
      date,
      dateEnd,
      groupID,
      hour,
      houseID,
      isTimed,
      minute,
      timedType,
      timedValue,
      title,
      liveStatus: '1'
    };

    // Another hack for the back-end, they want the selected option in an array of one item
    const mappedChannels = channels.map(channel => {
      delete channel.states;
      channel.states = [];
      channel.states.push(channel.value);
      return channel;
    });
    const channelReqBody = { channels: mappedChannels, ...ingestRequestBody };

    // See above
    const contactReqBody = { ...ingestRequestBody };
    contactReqBody.contacts = {};
    contactReqBody.contacts['1'] = consignmentContact;
    contactReqBody.contacts['3'] = saleAdministratorContact;
    contactReqBody.contacts['4'] = expertContact;

    const descriptionRequestBody = {
      viewNotes,
      description,
      resellUnsoldLiveLots,
      ...ingestRequestBody
    };

    if (type === 'live') {
      const { audioVideo } = this.state;
      descriptionRequestBody.audioVideo = audioVideo;
    }

    const defaultsReqBody = { ...ingestRequestBody };
    defaultsReqBody.contacts = {};

    if (consignContactCheckbox) {
      defaultsReqBody.contacts['1'] = consignmentContact;
      defaultsReqBody.defaultConsignContact = 1;
    }

    if (saleAdministratorContactCheckbox) {
      defaultsReqBody.contacts['3'] = saleAdministratorContact;
      defaultsReqBody.defaultSaleAdmin = 1;
    }

    if (expertContactCheckbox) {
      defaultsReqBody.contacts['4'] = expertContact;
      defaultsReqBody.defaultExpertContact = 1;
    }

    if (viewNotesCheckbox) {
      defaultsReqBody.viewNotes = viewNotes || '';
      defaultsReqBody.defaultViewNotes = 1;
    }

    /**
     * Add the requests which are always submitted before
     * appending conditional requests to the chain
     */
    const requestChain = [
      axios.post(`/cps/app/catalog/${catalogID}/categories`, [...selectedCategories], postHeaders),
      axios.post(`/cps/app/catalog/${catalogID}/channels`, channelReqBody, postHeaders),
      axios.post(`/cps/app/catalog/${catalogID}/contacts`, contactReqBody, postHeaders),
      axios.post(`/cps/app/catalog/${catalogID}/defaults`, defaultsReqBody, postHeaders),
      axios.post(`/cps/app/catalog/${catalogID}`, descriptionRequestBody, postHeaders),
      axios.post(`/cps/app/diy-step-tracker/${catalogID}`, stepNumber, postHeaders)
    ];

    try {
      await axios.all(requestChain);
      if (option === 'saveExit') {
        // Go back to the auction / sales page
        this.props.history.push('/my-auctions/current');
      } else {
        // Go to step 3 (or back to step 6 if you were editing)
        this.props.history.push(
          `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`
        );
      }
    } catch (error) {
      this.setState({ showSubmitError: true });
      window.scrollTo(0, 0);
      return null;
    }
  };

  render() {
    const { loading } = this.state;
    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    const {
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;
    const {
      audioVideo,
      bpSteps: { from: bpFrom, to: bpTo, stepPct: bpPct },
      categories,
      channels,
      consignmentContact,
      consignContactCheckbox,
      currencyCode,
      currencyName,
      description,
      expertContact,
      expertContactCheckbox,
      orderedConsigns,
      orderedExperts,
      orderedSaleAdmins,
      saleAdministratorContact,
      saleAdministratorContactCheckbox,
      selectedCategories,
      showLoadingError,
      showNoContactsError,
      showSubmitError,
      viewNotes,
      viewNotesCheckbox
    } = this.state;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step6';

    const selectedCategoriesSize = selectedCategories.size;

    // Test to see if the user has selected at least one "Available" option
    const userSelectedChannel = channels && channels.filter(channel => channel.value !== 0).length;
    // The option could also have been already selected - in which case it will appear as a label
    const liveOptionOnly = channels
      && channels.filter(channel => channel.states.length === 1
        && (channel.states[0] === 2 || channel.states[0] === 1)).length;
    const validateChannels = userSelectedChannel > 0 || liveOptionOnly > 0;

    // Prevent the user from continuing if they have not filled out the required fields
    const enableSubmit = selectedCategoriesSize >= 1
      && selectedCategoriesSize <= 8 && validateChannels;

    if (showLoadingError) {
      return <InlineAlert baseText={errorLoadingText} />;
    }

    if (showNoContactsError) {
      return <InlineAlert baseText={noContactsText} showPhones={false} />;
    }

    return (
      <div className="pb-5">
        {showSubmitError && <InlineAlert baseText={errorSubmittingText} />}
        <p>* = Required</p>

        <Form>
          <Row>
            {/* Start Left Column */}
            <Col xs={6}>
              {type.toLowerCase() !== 'timed' && (
                <FormGroup>
                  <Label className="mb-2" for="audioVideoBroadcast">
                    Audio Video Broadcast*
                  </Label>
                  <FontAwesomeIcon
                    id="audioVideoTooltip"
                    className="color-blue ml-1"
                    icon="question-circle"
                  />
                  <UncontrolledTooltip placement="right" target="audioVideoTooltip">
                    {audioVideoTooltipText}
                  </UncontrolledTooltip>
                  <FormGroup check>
                    <Label for="audioVideoOff" check>
                      <Input
                        id="audioVideoOff"
                        type="radio"
                        name="audioVideoRadio"
                        checked={audioVideo === 0}
                        onChange={() => this.handleAvRadio(0)}
                      />{' '}
                      Off
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mt-2">
                    <Label for="audioVideoOn" check>
                      <Input
                        id="audioVideoOn"
                        type="radio"
                        name="audioVideoRadio"
                        checked={audioVideo === 4}
                        onChange={() => this.handleAvRadio(4)}
                      />{' '}
                      On
                    </Label>
                  </FormGroup>
                </FormGroup>
              )}

              {isInteger(bpFrom) && (
                <FormGroup>
                  <Label for="buyersPremium">Buyer&apos;s Premium*</Label> (to change Buyer&apos;s Premium
                  edit your Auction Terms)
                  <p id="buyersPremium">
                    {bpFrom}-{bpTo} {currencyCode} = {bpPct}%
                  </p>
                </FormGroup>
              )}

              {currencyName && (
                <FormGroup>
                  <Label for="currency">Currency*</Label> (to change currency edit your Auction
                  Terms)
                  <p id="currency">
                    {currencyName} ({currencyCode})
                  </p>
                </FormGroup>
              )}

              <FormGroup className="pt-4 mb-0">
                <h3>Post Sale To*</h3>
              </FormGroup>

              <Row>
                {channels
                  && channels.map(channel => (
                    <ChannelRadioGroup
                      key={channel.channelID}
                      id={channel.channelID}
                      states={channel.states}
                      title={channel.channelName}
                      type={type}
                      value={channel.value}
                      handleRadioChange={this.handleRadioChange}
                    />
                  ))}
              </Row>

              <FormGroup className="pt-5 mb-1">
                <h3>Contacts*</h3>
              </FormGroup>
              <Row className="mb-1">
                <Col xs={6}>
                  <Label for="consignmentContact" className="mb-2">
                    Consignment Contact
                  </Label>
                  <Input
                    id="consignmentContact"
                    className="mb-2"
                    type="select"
                    onChange={e => this.handleSelectContact(e)}
                    value={consignmentContact}
                  >
                    {orderedConsigns
                      && orderedConsigns.map(consign => (
                        <option key={consign.id} value={consign.id}>
                          {consign.firstname} {consign.lastname}
                        </option>
                      ))}
                  </Input>
                  <FormGroup check>
                    <Label for="consignContactCheckbox" check>
                      <Input
                        id="consignContactCheckbox"
                        type="checkbox"
                        checked={consignContactCheckbox}
                        onChange={e => this.handleCheckbox(e)}
                      />
                      <p>Default</p>
                    </Label>
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <Label for="saleAdministratorContact" className="mb-2">
                    Sale Administrator Contact
                  </Label>
                  <Input
                    id="saleAdministratorContact"
                    className="mb-2"
                    type="select"
                    onChange={e => this.handleSelectContact(e)}
                    value={saleAdministratorContact}
                  >
                    {orderedSaleAdmins
                      && orderedSaleAdmins.map(saleAdmin => (
                        <option key={saleAdmin.id} value={saleAdmin.id}>
                          {saleAdmin.firstname} {saleAdmin.lastname}
                        </option>
                      ))}
                  </Input>
                  <FormGroup check>
                    <Label for="saleAdministratorContactCheckbox" check>
                      <Input
                        id="saleAdministratorContactCheckbox"
                        type="checkbox"
                        checked={saleAdministratorContactCheckbox}
                        onChange={e => this.handleCheckbox(e)}
                      />
                      <p>Default</p>
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={6}>
                  <Label for="expertContact" className="mb-2">
                    Expert Contact
                  </Label>
                  <Input
                    id="expertContact"
                    className="mb-2"
                    type="select"
                    onChange={e => this.handleSelectContact(e)}
                    value={expertContact}
                  >
                    {orderedExperts
                      && orderedExperts.map(expert => (
                        <option key={expert.id} value={expert.id}>
                          {expert.firstname} {expert.lastname}
                        </option>
                      ))}
                  </Input>
                  <FormGroup check>
                    <Label for="expertContactCheckbox" check>
                      <Input
                        id="expertContactCheckbox"
                        type="checkbox"
                        checked={expertContactCheckbox}
                        onChange={e => this.handleCheckbox(e)}
                      />
                      <p>Default</p>
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <h3>Session Categories for Email Alerts*</h3>
                <p className="mb-1">You must select at least one, and no more than 8 categories.</p>
                <p>
                  <strong>{selectedCategoriesSize} of 8 selected</strong>
                </p>

                {categories.map((category, index) => (
                  <CategoryAccordion
                    handleCategories={this.handleCategories}
                    index={index}
                    isOpen={category.isOpen}
                    items={category.subcategories}
                    key={category.id}
                    length={categories.length}
                    selectedCategories={selectedCategories}
                    title={category.name}
                    toggleAccordion={this.toggleAccordion}
                  />
                ))}
              </FormGroup>
            </Col>
            {/* End Left Column */}

            {/* Start Right Column */}
            <Col xs={6}>
              <FormGroup>
                <Label for="description">Session Description</Label>
                <Input
                  id="description"
                  name="description"
                  rows="12"
                  type="textarea"
                  onChange={e => this.handleTextArea(e)}
                  value={description}
                />
              </FormGroup>
              <FormGroup className="mb-2">
                <Label for="viewNotes">Viewing Notes</Label> (No HTML or JavaScript)
                <Input
                  id="viewNotes"
                  maxLength="2500"
                  name="viewNotes"
                  rows="6"
                  onChange={e => this.handleTextArea(e)}
                  type="textarea"
                  value={viewNotes}
                />
              </FormGroup>
              <FormGroup check>
                <Label for="viewNotesCheckbox" check>
                  <Input
                    id="viewNotesCheckbox"
                    type="checkbox"
                    checked={viewNotesCheckbox}
                    onChange={e => this.handleCheckbox(e)}
                  />
                  <p>Default</p>
                </Label>
              </FormGroup>
            </Col>
            {/* End Right Column */}
          </Row>
        </Form>

        <div>
          <Button
            className={
              !enableSubmit ? 'cursor-not-allowed min-width-max-content' : 'min-width-max-content'
            }
            color="primary"
            role="link"
            disabled={!enableSubmit}
            onClick={() => this.submitStepTwo('continue')}
          >
            {isEditingSession ? 'Save & Return to Summary' : 'Continue'}
          </Button>
          {isEditingSession && (
            <Link
              className="ml-3"
              to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/6`}
            >
              Cancel &amp; Return to Summary
            </Link>
          )}

          {!isEditingSession && enableSubmit && (
            <Button
              className="ml-3"
              role="link"
              color="link"
              onClick={() => this.submitStepTwo('saveExit')}
            >
              Save &amp; Exit
            </Button>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(Step2);
